import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import styles from './GenericSelect.module.scss';

export interface GenericSelectOption {
    id: number | string;
    label: string;
    color?: string;
    rightLabel?: string | JSX.Element;
    icon?: JSX.Element;
    disabled?: boolean;
}

export interface GenericSelectProps {
    placeholder?: string;
    selected?: GenericSelectOption | null;
    options: GenericSelectOption[];
    onSelect: (option: GenericSelectOption) => void;
    className?: string;
    showRightLabelOnSelected?: boolean;
    disabled?: boolean;
    inlineRightLabel?: boolean;
}

const GenericSelect: FunctionComponent<GenericSelectProps> = ({
    placeholder,
    selected,
    options,
    onSelect,
    className,
    showRightLabelOnSelected,
    disabled,
    inlineRightLabel
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const handleSelect = (opt: GenericSelectOption) => {
        if (disabled || opt.disabled) {
            return;
        }

        onSelect(opt);
        setIsCollapsed(true);
    };

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.textContainer}>
                <span
                    className={clsx(styles.header, {
                        [styles.placeholder]: !selected,
                        [styles.headerInline]: inlineRightLabel && showRightLabelOnSelected && selected?.rightLabel
                    })}
                >
                    {selected?.color && (
                        <div
                            className={styles.color}
                            style={{ backgroundColor: selected.color }}
                        />
                    )}
                    <span className={styles.inlineLabelText}>{selected?.label ?? placeholder}</span>
                    {inlineRightLabel && showRightLabelOnSelected && selected?.rightLabel && (
                        <span className={styles.inlineRightLabel}>{selected.rightLabel}</span>
                    )}
                </span>
            </div>

            {!inlineRightLabel && showRightLabelOnSelected && selected?.rightLabel && (
                <div className={styles.headerRight}>{selected.rightLabel}</div>
            )}

            <div className={styles.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={styles.chevron}
                />
            </div>
        </div>
    );

    const list = (
        <div className={styles.listContainer}>
            {options.map((opt, index) => (
                <div
                    key={index}
                    className={clsx(styles.itemContainer, {
                        [styles.itemContainerDisabled]: opt.disabled
                    })}
                    style={{ borderColor: opt.color ?? '#ffffff' }}
                    onClick={() => handleSelect(opt)}
                >
                    <div className={styles.itemContent}>
                        <div
                            className={clsx(styles.serviceItemContainer, styles.serviceNameContainer, {
                                [styles.serviceNameContainerInline]: inlineRightLabel && opt.rightLabel
                            })}
                        >
                            <span className={clsx(styles.item, styles.sectionService)}>{opt.label}</span>
                            {inlineRightLabel && opt.rightLabel && (
                                <span className={styles.inlineRightLabel}>{opt.rightLabel}</span>
                            )}
                        </div>

                        {opt.rightLabel && !inlineRightLabel && (
                            <div className={styles.sectionDuration}>
                                <div className={styles.serviceItemContainer}>
                                    {opt.icon && <div className={styles.durationContainer}>{opt.icon}</div>}
                                </div>

                                <div className={styles.serviceItemContainer}>
                                    <span className={clsx(styles.item, styles.bold, styles.spacing)}>
                                        {opt.rightLabel}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <div className={clsx(styles.container, className)}>
                <div className={styles.dropdownContainer}>
                    {isCollapsed ? (
                        <div
                            className={clsx(styles.border, styles.collapsedContainer, styles.collapsedBorder)}
                            onClick={() => !disabled && setIsCollapsed(false)}
                        >
                            {header}
                        </div>
                    ) : (
                        <div
                            className={clsx(styles.border, styles.expandedContainer)}
                            onClick={() => !disabled && setIsCollapsed(true)}
                        >
                            {header}
                            {list}
                        </div>
                    )}
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default GenericSelect;
